import React, { useEffect, useRef, useState, useCallback, memo } from 'react';
import { GoogleMap, InfoWindowF, MarkerF, PolylineF, useJsApiLoader } from "@react-google-maps/api";
import io from 'socket.io-client';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle'; 
import { MapIcon } from '@heroicons/react/24/solid';
import { FaBicycle, FaCar, FaBoxOpen, FaHamburger } from 'react-icons/fa';
import { motion, useAnimation } from "framer-motion";
import { AiOutlineHome, AiOutlineCompass, AiOutlineExpand, AiOutlineGlobal, AiOutlineClose } from 'react-icons/ai';
import { toast } from 'sonner';
import { Navigate, useNavigate } from 'react-router-dom';

const GOOGLE_MAPS_API_KEY = "AIzaSyAHFoepvVjrlMUctcC4wn_VRpOznZBzmhA";
const SOCKET_SERVER_URL = 'https://wavetechservices.in';
const MIN_DISTANCE_THRESHOLD = 10;
const libraries = ['geometry', 'places'];

const RiderComingAnimation = memo(({ partnerName, eta }) => {
  const controls = useAnimation();

  useEffect(() => {
    controls.start({
      x: [-20, 20, -20],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: 'linear',
      },
    });
  }, [controls]);

  return (
    <div className="p-4 rounded-2xl bg-white shadow-lg w-full max-w-md mx-auto">
      <div className="flex items-center gap-2 mb-2">
        <FaBicycle className="text-red-500 text-xl" />
        <h2 className="text-lg font-semibold">
          {partnerName} is on the way!
        </h2>
      </div>
      <p className="text-gray-600 mb-4">
        Arriving in {eta !== null ? eta : '--'}
      </p>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden relative">
        <motion.div
          className="w-1/4 h-full bg-red-500 rounded-full absolute left-0"
          animate={controls}
        />
      </div>
    </div>
  );
});

const LiveTracking = memo(({ booking }) => {
  const [location, setLocation] = useState(null);
  const [partnerLocation, setPartnerLocation] = useState(null);
  const [routeCoordinates, setRouteCoordinates] = useState([]);
  const [distance, setDistance] = useState(null);
  const [eta, setEta] = useState(null);
  const [error, setError] = useState(null);
  const [partnerName, setPartnerName] = useState('Service Partner');
  const [isLoading, setIsLoading] = useState(true);
  const [isRouteLoading, setIsRouteLoading] = useState(false);
  const [rideStatus, setRideStatus] = useState('pending');
  const [serviceIcon, setServiceIcon] = useState(FaBicycle);
  const [mapType, setMapType] = useState('roadmap');
  const [initialRegion, setInitialRegion] = useState(null);
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const socketRef = useRef(null);
  const lastPartnerLocationRef = useRef(null);
  const isMountedRef = useRef(true);

  // Utility functions
  const deg2rad = useCallback((deg) => deg * (Math.PI / 180), []);

  const calculateDistance = useCallback((loc1, loc2) => {
    if (!loc1 || !loc2) return Infinity;
    const R = 6371e3;
    const dLat = deg2rad(loc2.latitude - loc1.latitude);
    const dLon = deg2rad(loc2.longitude - loc1.longitude);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(deg2rad(loc1.latitude)) *
      Math.cos(deg2rad(loc2.latitude)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }, [deg2rad]);

  const calculateHeading = useCallback((prevLoc, newLoc) => {
    if (!prevLoc || !newLoc) return 0;
    const dLon = deg2rad(newLoc.longitude - prevLoc.longitude);
    const y = Math.sin(dLon) * Math.cos(deg2rad(newLoc.latitude));
    const x =
      Math.cos(deg2rad(prevLoc.latitude)) *
      Math.sin(deg2rad(newLoc.latitude)) -
      Math.sin(deg2rad(prevLoc.latitude)) *
      Math.cos(deg2rad(newLoc.latitude)) *
      Math.cos(dLon);
    const heading = (Math.atan2(y, x) * 180) / Math.PI;
    return (heading + 360) % 360;
  }, [deg2rad]);

  const showBothLocations = useCallback(() => {
    if (location && partnerLocation && mapRef.current) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend({ lat: location.latitude, lng: location.longitude });
      bounds.extend({ lat: partnerLocation.latitude, lng: partnerLocation.longitude });
      routeCoordinates.forEach(coord => {
        bounds.extend({ lat: coord.latitude, lng: coord.longitude });
      });
      mapRef.current.fitBounds(bounds);
    }
  }, [location, partnerLocation, routeCoordinates]);

  const centerOnUser = useCallback(() => {
    if (location && mapRef.current) {
      mapRef.current.panTo({ lat: location.latitude, lng: location.longitude });
      mapRef.current.setZoom(14);
    }
  }, [location]);

  const centerOnPartner = useCallback(() => {
    if (partnerLocation && mapRef.current) {
      mapRef.current.panTo({ lat: partnerLocation.latitude, lng: partnerLocation.longitude });
      mapRef.current.setZoom(14);
    }
  }, [partnerLocation]);

  const toggleMapType = useCallback(() => {
    setMapType(prev => prev === 'roadmap' ? 'satellite' : 'roadmap');
  }, []);

  // Socket event handlers
  const handlePartnerLocationUpdate = useCallback(throttle(({ latitude, longitude, timestamp }) => {
    if (!isMountedRef.current || !latitude || !longitude) return;

    const newPartnerLocation = {
      latitude,
      longitude,
      heading: lastPartnerLocationRef.current
        ? calculateHeading(lastPartnerLocationRef.current, { latitude, longitude })
        : 0,
      timestamp,
    };

    if (lastPartnerLocationRef.current &&
      calculateDistance(lastPartnerLocationRef.current, newPartnerLocation) < MIN_DISTANCE_THRESHOLD) {
      return;
    }

    lastPartnerLocationRef.current = newPartnerLocation;
    setPartnerLocation(newPartnerLocation);
    setIsRouteLoading(false);
  }, 1000), [calculateDistance, calculateHeading]);

  const handleRoutePolylineUpdate = useCallback(debounce(({ polylineCoordinates }) => {
    if (polylineCoordinates) {
      const coordinates = polylineCoordinates.filter(
        (coord) => coord.latitude && coord.longitude
      );
      if (coordinates.length > 0) {
        setRouteCoordinates(coordinates);
        setIsRouteLoading(false);
      }
    }
  }, 1000), []);

  const handleRouteInfo = useCallback(({ eta }) => {
    setDistance(eta?.formattedRemainingDistance || '-- km');
    setEta(eta?.formattedRemainingTime || '-- min');
    setRideStatus('active');
    setIsRouteLoading(false);
  }, []);

  const handleEtaUpdate = useCallback(({ eta }) => {
    setDistance(eta?.formattedRemainingDistance || '-- km');
    setEta(eta?.formattedRemainingTime || '-- min');
    setIsRouteLoading(false);
  }, []);

  const handleDriverArrived = useCallback(({ isFinalDestination }) => {
    if (isFinalDestination) {
      setRideStatus('arrived');
    }
  }, []);

  const handleRideStarted = useCallback(() => {
    setError(null);
    setIsRouteLoading(false);
  }, []);

  const handleRideEnded = useCallback(({ message }) => {
    toast.error(`${message}`);
    // navigate("/booking_list")
    window.location.href = '/booking_list'
    return
  }, []);

  const handleRideCheckResponse = useCallback(({ driverLocation }) => {
    if (driverLocation?.latitude && driverLocation?.longitude) {
      const initialPartnerLocation = {
        latitude: driverLocation.latitude,
        longitude: driverLocation.longitude,
        heading: 0,
        timestamp: Date.now(),
      };
      lastPartnerLocationRef.current = initialPartnerLocation;
      setPartnerLocation(initialPartnerLocation);
      setIsLoading(false);
      setIsRouteLoading(false);
    }
  }, []);

  // Socket connection management
  const setupSocket = useCallback(() => {
    const user = JSON.parse(localStorage.getItem("user")) || {};
    if (!user.userId) {
      setError('User data missing for tracking');
      setIsLoading(false);
      return;
    }

    // Clean up existing socket
    if (socketRef.current) {
      socketRef.current.off();
      socketRef.current.disconnect();
    }

    socketRef.current = io(SOCKET_SERVER_URL, {
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
      timeout: 10000,
      transports: ['websocket'],
    });

    socketRef.current.on('connect', () => {
      socketRef.current.emit('join', user.userId);
      socketRef.current.emit('ride_check', { rideId: booking?._id });
      setIsRouteLoading(true);
    });

    socketRef.current.on('ride_check_response', handleRideCheckResponse);
    socketRef.current.on('driver_location_update', handlePartnerLocationUpdate);
    socketRef.current.on('route_polyline_update', handleRoutePolylineUpdate);
    socketRef.current.on('route_info', handleRouteInfo);
    socketRef.current.on('eta_update', handleEtaUpdate);
    socketRef.current.on('driver_arrived', handleDriverArrived);
    socketRef.current.on('ride_started', handleRideStarted);
    socketRef.current.on('ride_ended', handleRideEnded);

    socketRef.current.on('connect_error', (err) => {
      console.error('Socket connection error:', err.message);
      setError('Failed to connect to tracking server');
      setIsRouteLoading(false);
    });

    socketRef.current.on('error', ({ message }) => {
      console.error('Socket error:', message);
      setError(message ? 'Tracking error: ' + message : 'Tracking error');
      setIsRouteLoading(false);
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.off();
        socketRef.current.disconnect();
      }
    };
  }, [
    booking?._id,
    handleRideCheckResponse,
    handlePartnerLocationUpdate,
    handleRoutePolylineUpdate,
    handleRouteInfo,
    handleEtaUpdate,
    handleDriverArrived,
    handleRideStarted,
    handleRideEnded
  ]);

  // Initialize tracking
  useEffect(() => {
    isMountedRef.current = true;

    if (!booking?.lat || !booking?.lng) {
      setError('Invalid booking coordinates');
      setIsLoading(false);
      return;
    }

    const userLocation = {
      latitude: parseFloat(booking.lat),
      longitude: parseFloat(booking.lng)
    };
    setLocation(userLocation);
    setInitialRegion({
      latitude: userLocation.latitude,
      longitude: userLocation.longitude,
      latitudeDelta: 0.05,
      longitudeDelta: 0.05,
    });

    // Set partner info
    if (booking?.partner) {
      setPartnerName(booking.partner?.profile?.name || 'Service Partner');
      
      if (booking.serviceType) {
        const icon = 
          booking.serviceType.toLowerCase().includes('food') ? FaHamburger :
          booking.serviceType.toLowerCase().includes('taxi') ? FaCar :
          booking.serviceType.toLowerCase().includes('delivery') ? FaBoxOpen :
          FaBicycle;
        setServiceIcon(icon);
      }
    }

    setupSocket();

    return () => {
      isMountedRef.current = false;
      if (socketRef.current) {
        socketRef.current.off();
        socketRef.current.disconnect();
      }
    };
  }, [booking, setupSocket]);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries
  });

  if (!isLoaded) return <div className="flex items-center justify-center h-screen">Loading Map...</div>;

  return (
    <div className="relative w-full h-screen bg-gray-900 overflow-hidden">
      {/* Loading Overlay */}
      {false ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900/95 z-50 backdrop-blur-sm">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-amber-400 rounded-full animate-ping absolute inset-0 opacity-75"></div>
            <div className="w-16 h-16 border-4 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
          <p className="mt-6 text-amber-400 text-lg font-medium tracking-wider">LOADING MAP...</p>
          <p className="text-gray-300 text-sm mt-2">Preparing real-time tracking</p>
        </div>
      ) : (
        <>
          {/* Main Map Container */}
          <div className="absolute inset-0">
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={{ 
                lat: initialRegion?.latitude || 0, 
                lng: initialRegion?.longitude || 0 
              }}
              zoom={15}
              options={{
                disableDefaultUI: true,
                styles: [
                  {
                    featureType: "all",
                    elementType: "labels",
                    stylers: [{ visibility: "off" }]
                  },
                  {
                    featureType: "road",
                    elementType: "geometry",
                    stylers: [{ color: "#2d3748" }]
                  },
                  {
                    featureType: "water",
                    elementType: "geometry",
                    stylers: [{ color: "#1a365d" }]
                  },
                  {
                    featureType: "landscape",
                    elementType: "geometry",
                    stylers: [{ color: "#1a202c" }]
                  },
                  {
                    featureType: "poi",
                    elementType: "geometry",
                    stylers: [{ color: "#2d3748" }]
                  }
                ],
                backgroundColor: "#1a202c",
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
                mapTypeId: mapType
              }}
              onLoad={(map) => {
                mapRef.current = map;
                showBothLocations();
              }}
            > 
 
              {/* User Marker */}
              {location && (
                <MarkerF 
                  position={{ lat: location.latitude, lng: location.longitude }}
                  icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 10,
                    fillColor: "#4285F4", // Google blue
                    fillOpacity: 1,
                    strokeWeight: 2,
                    strokeColor: "white"
                  }}
                >
                  <InfoWindowF position={{ lat: location.latitude, lng: location.longitude }}>
                    <div className="bg-gray-900 text-white p-2 rounded-lg shadow-xl">
                      <p className="font-bold">Your Location</p>
                    </div>
                  </InfoWindowF>
                </MarkerF>
              )}

              {/* Partner Marker */}
              {partnerLocation && (
                <MarkerF
                  position={{
                    lat: partnerLocation.latitude,
                    lng: partnerLocation.longitude
                  }}
                  icon={{
                    path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
                    fillColor: "#EA4335",
                    fillOpacity: 1,
                    strokeWeight: 0,
                    scale: 1.5,
                    anchor: new window.google.maps.Point(12, 24),
                    rotation: partnerLocation.heading || 0
                  }}
                  zIndex={1000}
                >
                  <InfoWindowF position={{ lat: partnerLocation.latitude, lng: partnerLocation.longitude }}>
                    <div className="bg-gray-900 text-white p-2 rounded-lg shadow-xl">
                      <p className="font-bold">{partnerName || 'Partner'}</p>
                      <p className="text-amber-400 text-sm">En route</p>
                    </div>
                  </InfoWindowF>
                </MarkerF>
              )}
 
              {/* Route Polyline */}
              {routeCoordinates.length > 0 && (
                <>
                  <PolylineF
                    path={routeCoordinates.map(coord => ({ lat: coord.latitude, lng: coord.longitude }))}
                    options={{
                      strokeColor: 'rgba(251, 191, 36, 0.8)',
                      strokeOpacity: 1.0,
                      strokeWeight: 8,
                      geodesic: true
                    }}
                  />
                  <PolylineF
                    path={routeCoordinates.map(coord => ({ lat: coord.latitude, lng: coord.longitude }))}
                    options={{
                      strokeColor: '#f59e0b',
                      strokeOpacity: 1.0,
                      strokeWeight: 4,
                      geodesic: true
                    }}
                  />
                </>
              )}
            </GoogleMap>
          </div>

          {/* Map Controls */}
          <div className="absolute right-4 top-4 flex flex-col space-y-3 z-10">
            <button 
              onClick={centerOnUser} 
              className="bg-gray-900/80 hover:bg-gray-800/90 p-3 rounded-full shadow-lg backdrop-blur-sm transition-all duration-300 transform hover:scale-105 border border-gray-700"
            >
              <AiOutlineHome size={22} className="text-amber-400" />
            </button>
            <button 
              onClick={centerOnPartner} 
              className="bg-gray-900/80 hover:bg-gray-800/90 p-3 rounded-full shadow-lg backdrop-blur-sm transition-all duration-300 transform hover:scale-105 border border-gray-700"
            >
              <AiOutlineCompass size={22} className="text-amber-400" />
            </button>
            <button 
              onClick={showBothLocations} 
              className="bg-gray-900/80 hover:bg-gray-800/90 p-3 rounded-full shadow-lg backdrop-blur-sm transition-all duration-300 transform hover:scale-105 border border-gray-700"
            >
              <AiOutlineExpand size={22} className="text-amber-400" />
            </button>
            <button 
              onClick={toggleMapType} 
              className="bg-gray-900/80 hover:bg-gray-800/90 p-3 rounded-full shadow-lg backdrop-blur-sm transition-all duration-300 transform hover:scale-105 border border-gray-700"
            >
              {mapType === 'roadmap' ? (
                <AiOutlineGlobal size={22} className="text-amber-400" />
              ) : (
                <MapIcon className="w-5 h-5 text-amber-400" />
              )}
            </button>
          </div>

          {/* Route Loading Indicator */}
          {isRouteLoading && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 px-4 py-3 bg-gray-900/80 backdrop-blur-sm border border-amber-400/30 rounded-lg shadow-lg z-10 flex items-center space-x-3 animate-pulse">
              <div className="w-5 h-5 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-amber-400 text-sm font-medium tracking-wider">UPDATING ROUTE...</p>
            </div>
          )}

          {/* Bottom Info Panel */}
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-gray-900 to-transparent z-10">
            <div className="bg-gray-900/80 backdrop-blur-sm rounded-2xl shadow-2xl border border-gray-800 overflow-hidden">
              {/* Partner Info */}
              <div className="p-4 border-b border-gray-800">
                <RiderComingAnimation partnerName={partnerName} eta={eta} />
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-2 divide-x divide-gray-800">
                <div className="p-4 text-center">
                  <p className="text-3xl font-bold text-amber-400">{distance || '--'}</p>
                  <p className="text-xs text-gray-400 uppercase tracking-wider mt-1">Distance</p>
                </div>
                <div className="p-4 text-center">
                  <p className="text-3xl font-bold text-amber-400">{eta !== null ? `${eta}` : '--'}</p>
                  <p className="text-xs text-gray-400 uppercase tracking-wider mt-1">ETA</p>
                </div>
              </div>
              
              {/* Call to Action */}
              <div className="p-4 bg-gray-800/50">
                <button className="w-full py-3 bg-amber-500 hover:bg-amber-600 rounded-lg text-gray-900 font-semibold transition-all duration-300 transform hover:scale-[1.02]">
                  Contact {partnerName}
                </button>
              </div>
            </div>
          </div>

          {/* Error Notification */}
          {error && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-red-600/90 backdrop-blur-sm text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-4 z-50 animate-fade-in">
              <div className="flex-1 text-sm font-medium tracking-wide">{error}</div>
              <button 
                onClick={() => setError(null)} 
                className="p-1 rounded-full hover:bg-white/10 transition-colors"
              >
                <AiOutlineClose size={18} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
});

export default LiveTracking;
